import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Sprout, ShoppingBag, Users, User, HelpCircle, LogOut, Library, Save, BookOpen } from 'lucide-react'
import { useGameStore } from '../store/gameStore'
import { useAuthStore } from '../store/authStore'
import { useI18n } from '../i18n/I18nProvider'
import Modal from './Modal'
import Button from './Button'
import InlineAuthForm from './InlineAuthForm'

import HerbariumModal from './herbarium/HerbariumModal'

function Header() {
  const { t } = useI18n()
  const user = useAuthStore((state) => state.user)
  const signOut = useAuthStore((state) => state.signOut)
  const seedInventory = useGameStore((state) => state.seedInventory)
  const plants = useGameStore((state) => state.plants)
  const herbarium = useGameStore((state) => state.herbarium)
  const resetAnonymousGarden = useGameStore((state) => state.resetAnonymousGarden)
  const navigate = useNavigate()
  const location = useLocation()
  const [showLeaveGardenModal, setShowLeaveGardenModal] = useState(false)
  const [nextPath, setNextPath] = useState(null)
  const [showAuthChoiceModal, setShowAuthChoiceModal] = useState(false)
  const [isHerbariumOpen, setIsHerbariumOpen] = useState(false)
  
  // Monnaie affichée dans le header : uniquement les graines basiques ('default')
  const basicSeeds = seedInventory?.default || 0

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const handleShopClick = (e) => {
    const totalSeeds = Object.values(seedInventory || {}).reduce((sum, qty) => sum + qty, 0)
    const hasProgress = (!user && ((plants && plants.length > 0) || totalSeeds > 0))

    if (location.pathname === '/garden' && hasProgress) {
      e.preventDefault()
      setNextPath('/shop')
      setShowLeaveGardenModal(true)
    }
  }

  return (
    <>
      <header className="bg-green-700 text-white shadow-lg">
      <div className="container mx-auto px-2 sm:px-4 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-2">
          <Link
            to={user ? '/garden' : '/'}
            className="flex items-center gap-1 sm:gap-2 hover:opacity-80 transition flex-shrink-0"
          >
            <Sprout size={28} className="sm:w-8 sm:h-8" />
            <span className="text-sm sm:text-xl font-bold pixel-font hidden sm:inline">
              {t('header.title')}
            </span>
          </Link>

          <nav className="flex items-center gap-1 sm:gap-3">
            {/* Afficher les graines basiques toujours (connecté ou non) */}
            <div className="bg-green-800 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2">
              <Sprout size={14} className="sm:w-4 sm:h-4" />
              <span className="font-bold text-sm sm:text-base">{basicSeeds}</span>
            </div>

            <button
              type="button"
              onClick={() => setIsHerbariumOpen(true)}
              className="relative p-1.5 sm:p-2 hover:bg-green-600 rounded-lg transition"
              title={t('herbarium.title')}
            >
              <BookOpen size={20} className="sm:w-6 sm:h-6" />
              <span className="absolute -top-1 -right-1 bg-white text-green-700 text-xs font-bold rounded-full px-1.5">
                {Object.keys(herbarium || {}).length || 0}
              </span>
            </button>

            <Link
              to="/garden"
              className="p-1.5 sm:p-2 hover:bg-green-600 rounded-lg transition"
              title={t('header.myGarden')}
            >
              <Sprout size={20} className="sm:w-6 sm:h-6" />
            </Link>
            
            {user && (
              <Link
                to="/shop"
                className="p-1.5 sm:p-2 hover:bg-green-600 rounded-lg transition"
                title={t('header.shop')}
                onClick={handleShopClick}
              >
                <ShoppingBag size={20} className="sm:w-6 sm:h-6" />
              </Link>
            )}
            
            <Link
              to="/help"
              className="p-1.5 sm:p-2 hover:bg-green-600 rounded-lg transition hidden sm:block"
              title={t('header.help')}
            >
              <HelpCircle size={20} className="sm:w-6 sm:h-6" />
            </Link>
            
            {user ? (
              <>
                <Link
                  to="/community"
                  className="p-1.5 sm:p-2 hover:bg-green-600 rounded-lg transition"
                  title={t('header.community')}
                >
                  <Users size={20} className="sm:w-6 sm:h-6" />
                </Link>
                
                <Link
                  to="/my-plants"
                  className="p-1.5 sm:p-2 hover:bg-green-600 rounded-lg transition"
                  title={t('header.myPublishedPlants')}
                >
                  <Library size={20} className="sm:w-6 sm:h-6" />
                </Link>
                
                <Link
                  to="/account"
                  className="p-1.5 sm:p-2 hover:bg-green-600 rounded-lg transition"
                  title={t('header.myAccount')}
                >
                  <User size={20} className="sm:w-6 sm:h-6" />
                </Link>
                
                <button
                  onClick={handleSignOut}
                  className="p-1.5 sm:p-2 hover:bg-red-600 rounded-lg transition hidden sm:block"
                  title={t('header.signOut')}
                >
                  <LogOut size={20} className="sm:w-6 sm:h-6" />
                </button>
              </>
            ) : (
              <Link
                to="/auth?mode=signup"
                className="bg-white text-green-700 px-2 py-1.5 sm:px-6 sm:py-2 rounded-lg font-bold hover:bg-green-50 transition text-xs sm:text-base flex items-center gap-1"
              >
                <Save size={16} className="sm:hidden" />
                <span className="hidden sm:inline">{t('auth.signUpButton')}</span>
                <span className="sm:hidden">{t('header.saveCtaMobile')}</span>
              </Link>
            )}
          </nav>
        </div>
      </div>
    </header>

      <Modal
        isOpen={showLeaveGardenModal}
        onClose={() => setShowLeaveGardenModal(false)}
        title={t('leaveGardenModal.title')}
        size="sm"
      >
        <p className="mb-4">
          {t('leaveGardenModal.body')}
        </p>
        <p className="mb-6">
          {t('leaveGardenModal.cta')}
        </p>

        <div className="space-y-3">
          <Button
            className="w-full"
            onClick={() => {
              setShowLeaveGardenModal(false)
              setShowAuthChoiceModal(true)
            }}
          >
            {t('leaveGardenModal.createAccount')}
          </Button>

          <Button
            variant="secondary"
            className="w-full"
            onClick={() => {
              setShowLeaveGardenModal(false)
              if (!user) {
                resetAnonymousGarden()
              }
              if (nextPath) {
                navigate(nextPath)
              }
            }}
          >
            {t('leaveGardenModal.quitAndLose')}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={showAuthChoiceModal}
        onClose={() => setShowAuthChoiceModal(false)}
        title={t('auth.signUpButton')}
        size="sm"
      >
        <InlineAuthForm
          initialMode="signup"
          shouldSave={true}
          onSuccess={() => {
            setShowAuthChoiceModal(false)
          }}
        />
      </Modal>

      <HerbariumModal
        isOpen={isHerbariumOpen}
        onClose={() => setIsHerbariumOpen(false)}
      />
    </>
  )
}

export default Header
